import * as React from 'react'
import { useMemo } from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react' // Import X icon if you want a clear button

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export interface ComboboxItem {
  value: string
  label: React.ReactNode
}

export interface ComboboxProps<T extends ComboboxItem = ComboboxItem> {
  /** Items to render */
  items: T[]
  /** Placeholder text when nothing is selected */
  placeholder?: string
  /** Selected value — provide _with_ onValueChange to make it controlled */
  value?: string | null // Allow null value
  /** Callback when selection changes */
  onValueChange?: (value: string | null) => void // Allow null value
  /** Disable filtering / search input */
  searchable?: boolean
  /** Extra classes to pass to the trigger button */
  className?: string
  /** Popover width (defaults to 200 px) */
  width?: number
  /** Allow the value to be cleared (set to null) */
  allowClear?: boolean // New property

  filter?: (value: string, search: string, keywords?: string[]) => number
}

export function Combobox<T extends ComboboxItem>({
  items,
  placeholder = 'Seleziona…',
  value,
  onValueChange,
  searchable = true,
  className,
  filter = (value, search) => (new RegExp(search, 'ig').test(value) ? 1 : -1),
  allowClear = false, // Default allowClear to false
}: ComboboxProps<T>) {
  const [open, setOpen] = React.useState(false)
  // Allow internal state to hold null
  const [internalValue, setInternalValue] = React.useState<string | null>(null)

  const isControlled = value !== undefined && onValueChange !== undefined
  // Handle potential undefined value for controlled case initially
  const currentValue = isControlled
    ? value === undefined
      ? null
      : value
    : internalValue
  const selectedOption = useMemo(
    // Ensure comparison works correctly with null
    () => items.find((i) => i.value === currentValue && currentValue !== null),
    [currentValue, items]
  )
  // Update setValue signature
  const setValue = (v: string | null) =>
    isControlled ? onValueChange!(v) : setInternalValue(v)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(`justify-between`, className)}
        >
          {/* Display placeholder if value is null or corresponding item not found */}
          {selectedOption?.label ?? placeholder}

          {allowClear && currentValue !== null ? (
            <div
              onClick={(e) => {
                e.preventDefault() // Prevent opening the popover
                e.stopPropagation()
                setValue(null)
              }}
            >
              <X className="ml-2 size-4 shrink-0 opacity-50 hover:opacity-100" />
            </div>
          ) : (
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={searchable} filter={filter}>
          {searchable && (
            <CommandInput placeholder={placeholder} className="h-9" />
          )}

          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>

            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  onSelect={(currentSelectedValue) => {
                    if (currentSelectedValue === currentValue) {
                      // If clicking the already selected item
                      setValue(allowClear ? null : '') // Set null if allowClear, else empty string (or keep current value if '' isn't desired)
                    } else {
                      // If clicking a new item
                      setValue(item.value) // Set to the new item's value
                    }
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      item.value === currentValue ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
